// Coupon Management System
// Handles user coupons, restrictions, and device-based storage

import { getDeviceInfo } from './deviceFingerprint';
import API_BASE_URL from '../config/api';

// Coupon types
export const COUPON_TYPES = {
  PERCENTAGE: 'percentage',
  FIXED: 'fixed',
  DELIVERY: 'delivery'
};

// Get user coupons from database
export const getUserCoupons = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    console.log('Fetching coupons for device:', deviceInfo.deviceId);
    const response = await fetch(`${API_BASE_URL}/api/user/coupons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch coupons');
    }
    
    const data = await response.json();
    console.log('API response:', data);
    return data.coupons || [];
  } catch (error) {
    console.error('Error fetching user coupons:', error);
    return [];
  }
};

// Add a new coupon to user's collection
export const addUserCoupon = async (coupon) => {
  try {
    const deviceInfo = getDeviceInfo();
    const couponData = {
      ...coupon,
      deviceInfo,
      expiryDays: 7, // 1 week expiry
      acquiredAt: new Date().toISOString()
    };
    
    const response = await fetch(`${API_BASE_URL}/api/user/coupons`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(couponData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to add coupon');
    }
    
    const data = await response.json();
    return data.coupon;
  } catch (error) {
    console.error('Error adding coupon:', error);
    throw error;
  }
};

// Use a coupon
export const useCoupon = async (couponId) => {
  try {
    console.log('🎫 useCoupon called with ID:', couponId);
    const deviceInfo = getDeviceInfo();
    console.log('🎫 Device info:', deviceInfo);
    
    const response = await fetch(`${API_BASE_URL}/api/user/coupons/${couponId}/use`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    console.log('🎫 Coupon usage response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('🎫 Coupon usage failed:', errorText);
      throw new Error('Failed to use coupon');
    }
    
    const result = await response.json();
    console.log('🎫 Coupon usage success:', result);
    return true;
  } catch (error) {
    console.error('🎫 Error using coupon:', error);
    return false;
  }
};

// Check if user can spin the wheel today
export const canSpinToday = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/spin/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to check spin status');
    }
    
    const data = await response.json();
    return data.canSpin;
  } catch (error) {
    console.error('Error checking spin status:', error);
    return false;
  }
};

// Record a spin
export const recordSpin = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/spin/record`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to record spin');
    }
    
    return true;
  } catch (error) {
    console.error('Error recording spin:', error);
    return false;
  }
};

// Check if user can play the game
export const canPlayGame = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/game/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to check game status');
    }
    
    const data = await response.json();
    return data.canPlay;
  } catch (error) {
    console.error('Error checking game status:', error);
    return false;
  }
};

// Record a game attempt
export const recordGameAttempt = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/game/attempt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to record game attempt');
    }
    
    return true;
  } catch (error) {
    console.error('Error recording game attempt:', error);
    return false;
  }
};

// Record a correct game answer (ends game session)
export const recordCorrectGameAttempt = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/game/attempt-correct`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to record correct game attempt');
    }
    
    return true;
  } catch (error) {
    console.error('Error recording correct game attempt:', error);
    return false;
  }
};

// Get game attempts remaining
export const getGameAttemptsRemaining = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/game/attempts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to get game attempts');
    }
    
    const data = await response.json();
    return data.attemptsRemaining;
  } catch (error) {
    console.error('Error getting game attempts:', error);
    return 0;
  }
};

// Get time until next game reset (in seconds)
export const getTimeUntilGameReset = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/game/reset-time`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to get reset time');
    }
    
    const data = await response.json();
    return data.secondsUntilReset;
  } catch (error) {
    console.error('Error getting reset time:', error);
    return 0;
  }
};

// Validate coupon for checkout
export const validateCouponForCheckout = async (couponCode, subtotal) => {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await fetch(`${API_BASE_URL}/api/user/coupons/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...deviceInfo,
        couponCode,
        subtotal
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to validate coupon');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error validating coupon:', error);
    return { valid: false, error: 'Failed to validate coupon' };
  }
};

// Calculate coupon discount
export const calculateCouponDiscount = (coupon, subtotal, deliveryCharge = 0) => {
  if (!coupon) return 0;
  
  switch (coupon.type) {
    case COUPON_TYPES.PERCENTAGE:
      return (subtotal * coupon.discount) / 100;
    case COUPON_TYPES.FIXED:
      return coupon.discount;
    case COUPON_TYPES.DELIVERY:
      return deliveryCharge;
    default:
      return 0;
  }
};

// Get coupon instructions
export const getCouponInstructions = (coupon, lang = 'en') => {
  const ar = lang === 'ar';
  const instructions = [];

  if (coupon.minAmount) {
    instructions.push(ar ? `الحد الأدنى للطلب: ${coupon.minAmount} ر.ع` : `Minimum order: ${coupon.minAmount} OMR`);
  }

  if (coupon.expiresAt) {
    const expiryDate = new Date(coupon.expiresAt);
    const daysUntilExpiry = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 0) {
      instructions.push(ar ? '⚠️ منتهي الصلاحية' : '⚠️ Expired');
    } else if (daysUntilExpiry <= 1) {
      instructions.push(ar ? '⚠️ ينتهي اليوم' : '⚠️ Expires today');
    } else if (daysUntilExpiry <= 3) {
      instructions.push(ar ? `⚠️ ينتهي خلال ${daysUntilExpiry} أيام` : `⚠️ Expires in ${daysUntilExpiry} days`);
    } else {
      instructions.push(ar ? `ينتهي: ${expiryDate.toLocaleDateString('ar-OM')}` : `Expires: ${expiryDate.toLocaleDateString()}`);
    }
  }

  if (coupon.used) {
    instructions.push(ar ? '✅ مستخدم' : '✅ Used');
  }

  return instructions;
};

// Get coupon statistics
export const getCouponStats = async () => {
  try {
    const deviceInfo = getDeviceInfo();
    console.log('Fetching coupon stats for device:', deviceInfo.deviceId);
    const response = await fetch(`${API_BASE_URL}/api/user/coupons/stats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error('Failed to get coupon stats');
    }
    
    const data = await response.json();
    console.log('Stats API response:', data);
    return data.stats;
  } catch (error) {
    console.error('Error getting coupon stats:', error);
    return {
      total: 0,
      used: 0,
      active: 0,
      expired: 0
    };
  }
}; 